import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from '@features/auth/contexts/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@shared/components/ui/tabs';
import { StockHistoryManager } from "../components/StockHistoryManager";
import { StockAnalyticsDashboard } from "../components/StockAnalyticsDashboard";
import { BulkStockHistoryImport } from "../components/BulkStockHistoryImport";
import { StockValidationPanel } from "../components/StockValidationPanel";
import {
  History,
  BarChart3,
  Upload,
  Shield,
  Package,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
} from "lucide-react";
import API from "@core/api/api";

export default function StockHistoryPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { id: productId } = useParams();
  const [activeTab, setActiveTab] = useState("history");
  
  // Check if user can edit stock movements (admin or manager only)
  const canEditStock = user?.role === "admin" || user?.role === "manager";

  // Fetch product details
  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => {
      const response = await API.get(`/products/${productId}`);
      return response.data;
    },
    enabled: !!productId,
  });

  // Fetch stock calculation
  const { data: stockCalculation } = useQuery({
    queryKey: ["stockCalculation", productId],
    queryFn: async () => {
      const response = await API.get(`/products/${productId}/stock/calculate`);
      return response.data;
    },
    enabled: !!productId,
  });

  const getStockHealthColor = (health) => {
    const colors = {
      healthy: "bg-green-100 text-green-800",
      moderate: "bg-yellow-100 text-yellow-800",
      low: "bg-orange-100 text-orange-800",
      critical: "bg-red-100 text-red-800",
      out_of_stock: "bg-gray-100 text-gray-800",
    };
    return colors[health] || "bg-gray-100 text-gray-800";
  };

  const getStockHealthIcon = (health) => {
    switch (health) {
      case "healthy":
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case "moderate":
        return <Activity className="h-4 w-4 text-yellow-600" />;
      case "low":
      case "critical":
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case "out_of_stock":
        return <Package className="h-4 w-4 text-gray-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  if (productLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                {t("Product Not Found")}
              </h2>
              <p className="text-muted-foreground">
                {t("The requested product could not be found.")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("Stock History")} - {product.name}
          </h1>
          <p className="text-muted-foreground">
            {t("Manage and analyze stock movements for this product")}
          </p>
        </div>
        <div className="flex gap-2">
          <BulkStockHistoryImport productId={productId} />
          <StockValidationPanel productId={productId} />
        </div>
      </div>

      {/* Stock Summary */}
      {stockCalculation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {t("Current Stock Status")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <div className="flex items-center justify-center gap-2 mb-2">
                  {getStockHealthIcon(stockCalculation?.stockHealth)}
                  <Badge
                    className={getStockHealthColor(
                      stockCalculation?.stockHealth
                    )}
                  >
                    {t(
                      stockCalculation?.stockHealth?.replace("_", " ") ||
                        "Unknown"
                    )}
                  </Badge>
                </div>
                <p className="text-2xl font-bold">
                  {stockCalculation?.calculatedStock || 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("Current Stock")}
                </p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-600">
                  +{stockCalculation?.totalIn || 0}
                </p>
                <p className="text-sm text-muted-foreground">{t("Total In")}</p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <TrendingDown className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-600">
                  -{stockCalculation?.totalOut || 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("Total Out")}
                </p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <Activity className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold">
                  {stockCalculation?.movementCount || 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("Movements")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${canEditStock ? 'grid-cols-4' : 'grid-cols-2'}`}>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t("History")}
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {t("Analytics")}
          </TabsTrigger>
          {canEditStock && (
            <TabsTrigger value="import" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              {t("Import")}
            </TabsTrigger>
          )}
          {canEditStock && (
            <TabsTrigger value="validation" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              {t("Validation")}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="history" className="space-y-6">
          <StockHistoryManager productId={productId} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <StockAnalyticsDashboard productId={productId} />
        </TabsContent>

        {canEditStock && (
          <TabsContent value="import" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  {t("Bulk Import")}
                </CardTitle>
                <CardDescription>
                  {t("Import multiple stock movements at once")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center py-8">
                    <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      {t("Bulk Stock History Import")}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {t(
                        "Use the bulk import feature to add multiple stock movements efficiently"
                      )}
                    </p>
                    <BulkStockHistoryImport productId={productId} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canEditStock && (
          <TabsContent value="validation" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  {t("Stock Validation")}
                </CardTitle>
                <CardDescription>
                  {t(
                    "Validate and correct stock movements to ensure data integrity"
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      {t("Stock Movement Validation")}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {t(
                        "Run validation checks to ensure stock movements are accurate and consistent"
                      )}
                    </p>
                    <StockValidationPanel productId={productId} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{t("Quick Actions")}</CardTitle>
          <CardDescription>
            {t("Common actions for stock management")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-2"
            >
              <History className="h-6 w-6" />
              <div className="text-center">
                <p className="font-medium">{t("View History")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("Browse all stock movements")}
                </p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-2"
            >
              <BarChart3 className="h-6 w-6" />
              <div className="text-center">
                <p className="font-medium">{t("Analytics")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("View detailed analytics")}
                </p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-2"
            >
              <Shield className="h-6 w-6" />
              <div className="text-center">
                <p className="font-medium">{t("Validate")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("Check data integrity")}
                </p>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
